import { FC, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Alert, AlertTitle, InputAdornment, Link, Stack, TextField, Typography, useTheme } from '@mui/material';
import GlobalContext from '../../contexts/GlobalContext';
import LoginContext, { ELoginUiState } from '../../contexts/LoginContext';
import { Link as RouterLink } from 'react-router-dom';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import styles from './LoginConsumer.module.scss';
import { EMixpanelEvent, ERoutes } from '../../util/constants';
import PasswordField from '../PasswordInput/PasswordInput';
import ScreenHeader from '../ScreenHeader/ScreenHeader';
import { useHistory } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { TForgotPasswordRouterState } from '../../contexts/ForgotPasswordContext';

const LoginConsumer: FC = () => {
    const { language } = useContext(GlobalContext);
    const {
        setLoginUiState,
        loginUiState,
        username,
        setUsername,
        usernameIsValid,
        password,
        setPassword,
        passwordIsValid,
        passwordValidationMessage,
        submitLoginForm,
        loginErrorTitle,
        loginErrorMessage,
        loginErrorType,
    } = useContext(LoginContext);
    const history = useHistory();
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);

    const handleChangeEmail = () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_email_edit' });
        setLoginUiState(ELoginUiState.ready);
        history.goBack();
    };

    const handleLogin = async (event: SyntheticEvent) => {
        event.preventDefault();
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin' });
        setIsLoading(true);
        await submitLoginForm();
        setIsLoading(false);
    };

    useEffect(() => {
        if (loginUiState === ELoginUiState.loginConsumer) {
            mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_signin' });
        }
    }, [loginUiState]);

    return (
        <PageLayoutV2 testId="login-consumer-form">
            <ScreenHeader title={`${language.common.hello},`} desc={language.login.enterPassword} />
            {loginUiState === ELoginUiState.error && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="login-error">
                    {loginErrorTitle && <AlertTitle sx={{ typography: 'subtitle2' }}>{loginErrorTitle}</AlertTitle>}
                    {loginErrorMessage}
                    {loginErrorType && (
                        <Typography
                            component={'div'}
                            color={'textSecondary'}
                            variant={'caption'}
                            sx={{ mt: 1 }}
                            data-testid="login-error-type"
                        >{`${language.common.error}: ${loginErrorType}`}</Typography>
                    )}
                </Alert>
            )}
            <form>
                <Stack spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label={language.login.username.consumer}
                        value={username}
                        onChange={(event) => setUsername(event.target.value)}
                        error={!usernameIsValid}
                        fullWidth
                        id="username"
                        size="small"
                        className={styles.email}
                        disabled
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Link
                                        onClick={() => {
                                            handleChangeEmail();
                                        }}
                                        sx={{ typography: 'subtitle2', cursor: 'pointer' }}
                                        data-testid="change-email"
                                        color={theme.palette.action.active}
                                    >
                                        {language.common.edit}
                                    </Link>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <PasswordField
                        value={password}
                        label={language.common.password}
                        error={!passwordIsValid}
                        onChange={(event) => {
                            setPassword(event.target.value);
                        }}
                        helperText={!passwordIsValid && passwordValidationMessage}
                        id="password"
                        autoFocus={true}
                        size="small"
                        onShowPassword={() => {
                            mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_password_show' });
                        }}
                        onHidePassword={() => {
                            mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_password_hide' });
                        }}
                    />
                    <LoadingButton
                        disabled={isLoading}
                        onClick={handleLogin}
                        loading={isLoading}
                        type="submit"
                        variant="contained"
                        size="large"
                    >
                        {language.login.loginCTA}
                    </LoadingButton>
                </Stack>
            </form>

            <Stack sx={{ my: 2 }} direction={'row'} alignItems={'center'} justifyContent={'flex-start'}>
                <Typography variant={'body2'}>{language.login.forgotPassword.desc}</Typography>
                <Link
                    sx={{ typography: 'subtitle2', ml: 1 }}
                    component={RouterLink}
                    onClick={() => {
                        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_forgot_password' });
                    }}
                    to={{
                        pathname: `${ERoutes.forgotPassword}`,
                        state: { email: username } as TForgotPasswordRouterState,
                    }}
                    color={theme.palette.text.primary}
                >
                    {language.login.forgotPassword.cta}
                </Link>
            </Stack>
        </PageLayoutV2>
    );
};

export default LoginConsumer;
